import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { skillsApi } from "@/api/skills";
import type { SkillCatalogueItem } from "@/api/skills";
import { queryKeys } from "@/lib/query-keys";
import {
  Zap,
  Plus,
  Trash2,
  Sparkles,
  X,
  Download,
  Calendar,
  Tag,
  Wrench,
  Pencil,
  Upload,
  Save,
} from "lucide-react";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";

/**
 * MSP (platform admin) skill catalogue. Adds two things that were missing:
 *   1. A SKILL.md textarea (+ "Upload .md file" picker) in the Create form
 *      so admins can paste a skill they built elsewhere.
 *   2. An Edit button on each skill card AND inside the detail modal —
 *      lets admins update name/description/SKILL.md after creation.
 *
 * The server (POST/PATCH /api/skill-catalogue) and the shared Zod validator
 * (`createSkillCatalogueSchema`, `updateSkillCatalogueSchema`) already
 * support `contentMd`; this change is UI-only.
 */

function SkillCard({
  item,
  onOpen,
  onEdit,
  onDelete,
}: {
  item: SkillCatalogueItem;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const stopAnd = (fn: () => void) => (e: React.SyntheticEvent) => {
    e.stopPropagation();
    fn();
  };
  return (
    <button
      onClick={onOpen}
      className="group flex h-full flex-col rounded-xl border border-border bg-surface p-5 text-left transition-all hover:border-accent hover:shadow-md"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
          <Zap size={20} className="text-accent" />
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant={item.status === "active" ? "success" : "default"}>
            {item.status}
          </Badge>
          {item.version && <Badge variant="secondary">{item.version}</Badge>}
        </div>
      </div>
      <h3 className="mb-1.5 text-base font-semibold text-text-primary">
        {item.displayName || item.name}
      </h3>
      {item.description && (
        <p className="mb-4 line-clamp-3 flex-1 text-sm text-text-secondary">
          {item.description}
        </p>
      )}
      <div className="mt-auto flex items-center justify-between border-t border-border pt-3">
        <span className="text-xs text-text-muted capitalize">{item.skillType}</span>
        <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <span
            role="button"
            tabIndex={0}
            onClick={stopAnd(onEdit)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onEdit();
              }
            }}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
            aria-label="Edit skill"
          >
            <Pencil size={14} />
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={stopAnd(onDelete)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onDelete();
              }
            }}
            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
            aria-label="Delete skill"
          >
            <Trash2 size={14} />
          </span>
        </div>
      </div>
    </button>
  );
}

function SkillDetailModal({
  item,
  initialEdit = false,
  onClose,
}: {
  item: SkillCatalogueItem;
  initialEdit?: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(initialEdit);
  const [draft, setDraft] = useState({
    name: item.name,
    displayName: item.displayName ?? "",
    description: item.description ?? "",
    contentMd: item.contentMd ?? "",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape (but only when not editing — don't let Escape silently
  // discard unsaved changes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !editing) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, editing]);

  const updateMutation = useMutation({
    mutationFn: () =>
      skillsApi.updateCatalogue(item.id, {
        name: draft.name.trim(),
        displayName: draft.displayName.trim() || null,
        description: draft.description.trim() || null,
        contentMd: draft.contentMd || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.skillCatalogue.all });
      setEditing(false);
    },
  });

  const handleDownload = () => {
    const content = item.contentMd ?? "";
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.name || "skill"}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFilePicked = async (file: File) => {
    if (file.size > 256 * 1024) {
      window.alert("File too large (max 256KB).");
      return;
    }
    const text = await file.text();
    setDraft((d) => ({ ...d, contentMd: text }));
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const dirty =
    editing &&
    (draft.name !== item.name ||
      (draft.displayName || "") !== (item.displayName ?? "") ||
      (draft.description || "") !== (item.description ?? "") ||
      (draft.contentMd || "") !== (item.contentMd ?? ""));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={editing ? undefined : onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent/10">
              <Zap size={24} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {item.displayName || item.name}
              </h2>
              <div className="mt-1.5 flex items-center gap-1.5">
                <Badge variant={item.status === "active" ? "success" : "default"}>
                  {item.status}
                </Badge>
                {item.version && <Badge variant="secondary">v{item.version}</Badge>}
                <Badge variant="default" className="capitalize">{item.skillType}</Badge>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-text-muted hover:bg-surface-hover hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Name
                </label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Display Name (optional)
                </label>
                <Input
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  placeholder={item.name}
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  Description
                </label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    SKILL.md
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={13} className="mr-1.5" />
                    Upload .md
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".md,.markdown,text/markdown"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFilePicked(f);
                      e.target.value = "";
                    }}
                  />
                </div>
                <textarea
                  value={draft.contentMd}
                  onChange={(e) => setDraft({ ...draft, contentMd: e.target.value })}
                  rows={18}
                  placeholder="Paste your SKILL.md content here..."
                  className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs leading-relaxed"
                />
                <p className="mt-1 text-[11px] text-text-muted">
                  Max 50,000 characters. You can also upload a .md file.
                </p>
              </div>
              {updateMutation.isError && (
                <p className="text-xs text-red-500">
                  Error: {(updateMutation.error as Error).message}
                </p>
              )}
            </div>
          ) : (
            <>
              {/* About section */}
              <section className="mb-6">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  About this skill
                </h3>
                {item.description ? (
                  <p className="text-sm leading-relaxed text-text-secondary">{item.description}</p>
                ) : (
                  <p className="text-sm italic text-text-muted">No description provided.</p>
                )}
              </section>

              {/* Metadata grid */}
              <section className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border bg-surface-hover p-4 sm:grid-cols-2">
                <div className="flex items-start gap-2">
                  <Tag size={14} className="mt-0.5 shrink-0 text-text-muted" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-text-muted">Type</p>
                    <p className="text-sm capitalize text-text-primary">{item.skillType}</p>
                  </div>
                </div>
                {item.version && (
                  <div className="flex items-start gap-2">
                    <Tag size={14} className="mt-0.5 shrink-0 text-text-muted" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-text-muted">Version</p>
                      <p className="text-sm text-text-primary">{item.version}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Calendar size={14} className="mt-0.5 shrink-0 text-text-muted" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-text-muted">Created</p>
                    <p className="text-sm text-text-primary">{formatDate(item.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar size={14} className="mt-0.5 shrink-0 text-text-muted" />
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-text-muted">Updated</p>
                    <p className="text-sm text-text-primary">{formatDate(item.updatedAt)}</p>
                  </div>
                </div>
                {item.requiredMcps && item.requiredMcps.length > 0 && (
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <Wrench size={14} className="mt-0.5 shrink-0 text-text-muted" />
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-text-muted">Required MCPs</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {item.requiredMcps.map((mcp) => (
                          <Badge key={mcp} variant="secondary">{mcp}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* SKILL.md content */}
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  SKILL.md
                </h3>
                {item.contentMd ? (
                  <div className="rounded-xl border border-border bg-background p-5">
                    <MarkdownRenderer content={item.contentMd} />
                  </div>
                ) : (
                  <div className="rounded-xl border border-border bg-surface-hover p-4">
                    <p className="text-sm italic text-text-muted">No SKILL.md content defined.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border bg-background px-6 py-4">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft({
                    name: item.name,
                    displayName: item.displayName ?? "",
                    description: item.description ?? "",
                    contentMd: item.contentMd ?? "",
                  });
                  setEditing(false);
                }}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => updateMutation.mutate()}
                disabled={!dirty || !draft.name.trim() || updateMutation.isPending}
              >
                <Save size={14} className="mr-1.5" />
                {updateMutation.isPending ? "Saving..." : "Save changes"}
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                disabled={!item.contentMd}
                title={item.contentMd ? "Download SKILL.md" : "No content to download"}
              >
                <Download size={14} className="mr-1.5" />
                Download
              </Button>
              <Button size="sm" onClick={() => setEditing(true)}>
                <Pencil size={14} className="mr-1.5" />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GenerateWithAIDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [description, setDescription] = useState("");

  const generateMutation = useMutation({
    mutationFn: () => skillsApi.generateCatalogue(description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.skillCatalogue.all });
      onClose();
    },
  });

  return (
    <Card className="mb-4">
      <CardContent className="space-y-3 pt-4">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-accent" />
          <h3 className="text-sm font-medium text-text-primary">Generate Skill with AI</h3>
        </div>
        <p className="text-xs text-text-muted">
          Describe the skill you want to create and AI will generate a complete SKILL.md with proper format and instructions.
        </p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the skill you want to create... e.g., 'A skill for managing Jira tickets, including creating, updating, and transitioning issues through workflows.'"
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          rows={4}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!description.trim() || generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
          >
            <Sparkles size={14} className="mr-1.5" />
            {generateMutation.isPending ? "Generating..." : "Generate Skill"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
        {generateMutation.isError && (
          <p className="text-xs text-red-500">
            Error: {(generateMutation.error as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CreateSkillDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    description: "",
    skillType: "builtin",
    contentMd: "",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      skillsApi.createCatalogue({
        name: form.name.trim(),
        skillType: form.skillType,
        description: form.description.trim() || undefined,
        contentMd: form.contentMd || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.skillCatalogue.all });
      onClose();
    },
  });

  const handleFilePicked = async (file: File) => {
    if (file.size > 256 * 1024) {
      window.alert("File too large (max 256KB).");
      return;
    }
    const text = await file.text();
    setForm((f) => ({ ...f, contentMd: text }));
  };

  return (
    <Card className="mb-4">
      <CardContent className="space-y-3 pt-4">
        <div className="flex gap-3">
          <Input
            placeholder="Skill name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="flex-1"
          />
          <select
            value={form.skillType}
            onChange={(e) => setForm({ ...form, skillType: e.target.value })}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
          >
            <option value="builtin">Built-in</option>
            <option value="custom">Custom</option>
            <option value="template">Template</option>
          </select>
        </div>
        <Input
          placeholder="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        {/* SKILL.md paste / upload */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              SKILL.md content (optional)
            </label>
            <Button
              size="sm"
              variant="outline"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={13} className="mr-1.5" />
              Upload .md
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,.markdown,text/markdown"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFilePicked(f);
                e.target.value = "";
              }}
            />
          </div>
          <textarea
            value={form.contentMd}
            onChange={(e) => setForm({ ...form, contentMd: e.target.value })}
            rows={12}
            placeholder="Paste a SKILL.md that you built in another agent, or upload a .md file."
            className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs leading-relaxed"
          />
          <p className="mt-1 text-[11px] text-text-muted">
            Leave blank if you just want a stub — you can paste content later from the Edit button.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            disabled={!form.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
        {createMutation.isError && (
          <p className="text-xs text-red-500">
            Error: {(createMutation.error as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function SkillCatalogue() {
  const queryClient = useQueryClient();
  const { data: items = [], isLoading } = useQuery({
    queryKey: queryKeys.skillCatalogue.all,
    queryFn: skillsApi.listCatalogue,
  });
  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [openSkillId, setOpenSkillId] = useState<string | null>(null);
  const [openInEditMode, setOpenInEditMode] = useState(false);

  const openSkill = items.find((s) => s.id === openSkillId) ?? null;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => skillsApi.deleteCatalogue(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.skillCatalogue.all }),
  });

  return (
    <CenterPanel title="Skill Catalogue">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {items.length} skill{items.length !== 1 ? "s" : ""} in catalogue
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowGenerate(!showGenerate);
              setShowCreate(false);
            }}
          >
            <Sparkles size={16} className="mr-1.5" />
            Generate with AI
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setShowCreate(!showCreate);
              setShowGenerate(false);
            }}
          >
            <Plus size={16} className="mr-1.5" />
            Add Skill
          </Button>
        </div>
      </div>

      {showGenerate && <GenerateWithAIDialog onClose={() => setShowGenerate(false)} />}
      {showCreate && <CreateSkillDialog onClose={() => setShowCreate(false)} />}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading catalogue...</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Zap size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No skills in catalogue yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <SkillCard
              key={item.id}
              item={item}
              onOpen={() => {
                setOpenSkillId(item.id);
                setOpenInEditMode(false);
              }}
              onEdit={() => {
                setOpenSkillId(item.id);
                setOpenInEditMode(true);
              }}
              onDelete={() => deleteMutation.mutate(item.id)}
            />
          ))}
        </div>
      )}

      {openSkill && (
        <SkillDetailModal
          item={openSkill}
          initialEdit={openInEditMode}
          onClose={() => {
            setOpenSkillId(null);
            setOpenInEditMode(false);
          }}
        />
      )}
    </CenterPanel>
  );
}
